require('./cmFundsSummaryStep1.scss');

const PropTypes = require('prop-types');

// @vendor
const React = require('react');
const classnames = require('classnames');

// @commons
const { OKButton } = require('components/commons/OKButton/OKButton');
const { OKTable } = require('commonsComponents/OKTable');
const { OKTableRow } = require('commonsComponents/OKTableRow');
const { OKTableCell } = require('commonsComponents/OKTableCell');
const OKTooltip = require('components/commons/OKTooltip/OKTooltip');
const { OKLink } = require('commonsComponents/OKLink');

// @components
const {
    OneLineSymbolAmount,
} = require('commonsComponents/oneLineSymbolAmount');
const FormattedTextWithComponents = require('components/broker/brokerFormattedTextWithComponents/brokerFormattedTextWithComponents');

// @helpers
const { FormattedText, getLanguage } = require('core/i18n').i18n;
const { formatAmountObject } = require('utilities/currencyHelper');
const { buildLinks } = require('utilities/fundsHelper');

// @constants
const {
    CM_ACTIVE_CLASS_MONEY_MARKET,
    CM_ACTIVE_CLASS_FIXED_INCOME,
    CM_ACTIVE_CLASS_EQUITY,
    CM_ACTIVE_CLASS_REAL_ASSETS,
} = require('../../../constants/constants');

class CmFundsSummaryStep1 extends React.Component {
    constructor(props) {
        super(props);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.getRowAmount = this.getRowAmount.bind(this);
        this.buildDocumentationCell = this.buildDocumentationCell.bind(this);
    }

    handleDocumentClick(isin) {
        this.props.onLegalDocumentClick(isin);
    }

    sumAmounts(assets) {
        let totalAmount = 0;
        let currency;

        assets.forEach(asset => {
            const subClasses = asset.get('subAssetClassInfo');
            subClasses.forEach(subClass => {
                const subAssets = subClass.get('subAssetClassIsinInfo');
                currency = subAssets.getIn([0, 'currency']);
                subAssets.forEach(subAsset => {
                    totalAmount += subAsset.get('subAssetClassFundAmount');
                });
            });
        });

        return {
            amount: totalAmount,
            currency,
        };
    }

    getRowClass(assetClass) {
        const customClass = classnames('cm-funds-summary__row', {
            ['cm-funds-summary__row--money-market']:
                assetClass === CM_ACTIVE_CLASS_MONEY_MARKET,
            ['cm-funds-summary__row--fixed-income']:
                assetClass === CM_ACTIVE_CLASS_FIXED_INCOME,
            ['cm-funds-summary__row--equity']:
                assetClass === CM_ACTIVE_CLASS_EQUITY,
            ['cm-funds-summary__row--real-assets']:
                assetClass === CM_ACTIVE_CLASS_REAL_ASSETS,
        });

        return customClass;
    }

    getRowAmount(subAsset) {
        return {
            amount: subAsset.get('subAssetClassFundAmount'),
            currency: subAsset.get('currency'),
        };
    }

    getInvalidMessage(isValid) {
        if (
            isValid === false &&
            this.props.showGeneralMinAmountMessage &&
            !this.props.isFetchingPortfolioInfo
        ) {
            return <span className="cm-funds-summary__error">*</span>;
        }
    }

    getInvalidFundMessage(isValid, minFundAmount) {
        if (
            isValid === false &&
            minFundAmount &&
            this.props.showGeneralMinAmountMessage &&
            !this.props.isFetchingPortfolioInfo
        ) {
            const amount = {
                amount: minFundAmount,
                currency: 'EUR',
            };

            return (
                <div className="cm-funds-summary__error__message">
                    {' '}
                    <i className="icon cm-funds-summary__error__message--icon" />
                    <FormattedTextWithComponents
                        value="brokerCM-buyStep1MinFundAmountPerFundError"
                        className="cm-funds-summary__error__message"
                        injectedComponents={[
                            <OneLineSymbolAmount
                                amountObject={amount}
                                key="cmWalletBuyStep2AvailableAmount"
                                language={getLanguage()}
                                amountObjectFormatted={formatAmountObject(
                                    amount,
                                    true,
                                )}
                            />,
                        ]}
                    />
                </div>
            );
        }
    }

    buildTableHeader() {
        let legalDocumentHeader;
        let secondHeaderClass;
        if (!this.props.hideDocument) {
            legalDocumentHeader = (
                <div>
                    <FormattedText
                        value="brokerCM-fundsSummaryLegalDocumentHeader"
                        className="cm-funds-summary__header-document-content"
                    />
                    <OKTooltip
                        offset={[0, -5]}
                        content="brokerCM-buyStep1Tooltip2"
                    />
                </div>
            );
        }
        const doNotHaveLegalDocumentColumnClassName = classnames(
            'cm-funds-summary__header-cell',
            {
                'cm-funds-summary__header-cell--first':
                    this.props.hideDocument === false,
                'cm-funds-summary__header-cell--hide-legal-column':
                    this.props.hideDocument === true,
            },
        );

        return (
            <OKTableRow
                asHeader={true}
                className="cm-funds-summary__row cm-funds-summary__header-row hidden-xs"
            >
                <OKTableCell
                    asHeader={true}
                    className={doNotHaveLegalDocumentColumnClassName}
                >
                    <FormattedText
                        value="brokerCM-fundsSummaryFundNameHeader"
                        className="cm-funds-summary__header-content"
                    />
                </OKTableCell>
                <OKTableCell
                    asHeader={true}
                    className={
                        'cm-funds-summary__header-cell ' + secondHeaderClass
                    }
                >
                    <FormattedText
                        value="brokerCM-fundsSummaryActiveClassHeader"
                        className="cm-funds-summary__header-content"
                    />
                </OKTableCell>
                <OKTableCell
                    asHeader={true}
                    className="cm-funds-summary__header-cell cm-funds-summary__header-cell--third"
                >
                    {legalDocumentHeader}
                </OKTableCell>
                <OKTableCell
                    asHeader={true}
                    className="cm-funds-summary__header-cell cm-funds-summary__header-cell--last"
                >
                    <FormattedText
                        value="brokerCM-fundsSummaryLegalAmountHeader"
                        className="cm-funds-summary__header-content"
                    />
                </OKTableCell>
            </OKTableRow>
        );
    }

    buildDocumentationCell(subAsset) {
        let legalDocument;
        if (!this.props.hideDocument) {
            legalDocument = (
                <div className="cm-funds-summary__icon-wrapper">
                    <OKButton
                        modifier="additional-action-secondary cm-funds-summary__cell cm-funds-summary__cell--third-button"
                        size="small"
                        loaderColor="dark"
                        loaderSize="small"
                        onClick={() =>
                            this.handleDocumentClick(
                                subAsset.get('subAssetClassIsin'),
                            )
                        }
                    >
                        <i className="icon-link-externo cm-funds-summary__icon"></i>
                    </OKButton>
                </div>
            );
        }
        return legalDocument;
    }

    productFileLink(isin) {
        return buildLinks(isin);
    }

    fundsLinking(subAsset) {
        const { isFundsLink } = this.props;
        if (isFundsLink) {
            return (
                <OKLink
                    target="_blank"
                    to={this.productFileLink(subAsset.get('subAssetClassIsin'))}
                >
                    {subAsset.get('subAssetClassFundName')}
                </OKLink>
            );
        } else {
            return subAsset.get('subAssetClassFundName');
        }
    }

    buildTableBody(assets) {
        const { originFundMatchedIsin } = this.props;
        let assetClass;
        let activeSubclass;
        let rowClass;
        let subAssets;

        const getSortingValue = (imm, key) => Number(imm.get(key).slice(-3));
        const sortedAssets = assets
            .map(asset =>
                asset.update('subAssetClassInfo', subAssetClassInfo =>
                    subAssetClassInfo.sort(
                        (a, b) =>
                            getSortingValue(a, 'subAssetClassId') -
                            getSortingValue(b, 'subAssetClassId'),
                    ),
                ),
            )
            .sort(
                (a, b) =>
                    getSortingValue(a, 'assetClassId') -
                    getSortingValue(b, 'assetClassId'),
            );

        return sortedAssets.map(
            asset => (
                (assetClass = asset.get('assetClassId')),
                (rowClass = this.getRowClass(assetClass)),
                asset.get('subAssetClassInfo').map(subClass => {
                    const assetClassDesc = asset.get('assetClassDesc');
                    activeSubclass = subClass.get('subAssetClassDesc');
                    subAssets = subClass.get('subAssetClassIsinInfo');

                    return subAssets.map((subAsset, index) => (
                        <OKTableRow
                            key={index}
                            className={
                                originFundMatchedIsin ===
                                subAsset.get('subAssetClassIsin')
                                    ? 'cm-funds-summary__row-matched'
                                    : ''
                            }
                        >
                            <OKTableCell
                                className={
                                    'cm-funds-summary__cell cm-funds-summary__cell--first ' +
                                    rowClass
                                }
                            >
                                <span className="cm-funds-summary__cell-content-first visible-xs">
                                    {assetClassDesc + ' - ' + activeSubclass}
                                </span>
                                <span className="cm-funds-summary__cell-content-first">
                                    {this.fundsLinking(subAsset)}
                                    {this.getInvalidFundMessage(
                                        subAsset.get('isValidAmount'),
                                        subAsset.get('fundMinAmount'),
                                    )}
                                </span>
                                <div className="cm-funds-summary__portfolio--footer visible-xs">
                                    <div className="cm-funds-summary__icon-wrapper">
                                        <FormattedText
                                            value="brokerCM-fundsSummaryLegalDocumentHeader"
                                            className="cm-funds-summary__header-document-content"
                                        />
                                        <OKButton
                                            modifier="additional-action-secondary"
                                            size="small"
                                            loaderColor="dark"
                                            loaderSize="small"
                                            onClick={() =>
                                                this.handleDocumentClick(
                                                    subAsset.get(
                                                        'subAssetClassIsin',
                                                    ),
                                                )
                                            }
                                        >
                                            <i className="icon-link-externo cm-funds-summary__icon"></i>
                                        </OKButton>
                                    </div>
                                    <div className="cm-funds-summary__amount-wrapper">
                                        <OneLineSymbolAmount
                                            amountObject={{
                                                amount: subAsset.get(
                                                    'subAssetClassFundAmount',
                                                ),
                                                currency: subAsset.get(
                                                    'currency',
                                                ),
                                            }}
                                            className="cm-funds-summary__cell-content-last"
                                            language={getLanguage()}
                                            amountObjectFormatted={formatAmountObject(
                                                subAsset.get(
                                                    'subAssetClassFundAmount',
                                                ),
                                                true,
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="cm-funds-summary__gutter-spacing visible-xs"></div>
                            </OKTableCell>
                            <OKTableCell className="cm-funds-summary__cell cm-funds-summary__cell--second hidden-xs">
                                <span className="cm-funds-summary__cell-content-second">
                                    {assetClassDesc + ' - ' + activeSubclass}
                                </span>
                            </OKTableCell>
                            <OKTableCell className="cm-funds-summary__cell cm-funds-summary__cell--third hidden-xs">
                                {this.buildDocumentationCell(subAsset)}
                            </OKTableCell>
                            <OKTableCell className="cm-funds-summary__cell cm-funds-summary__cell--last hidden-xs">
                                <OneLineSymbolAmount
                                    amountObject={this.getRowAmount(subAsset)}
                                    className="cm-funds-summary__cell-content-last"
                                    language={getLanguage()}
                                    amountObjectFormatted={formatAmountObject(
                                        this.getRowAmount(subAsset),
                                        true,
                                    )}
                                />
                                {this.getInvalidMessage(
                                    subAsset.get('isValidAmount'),
                                )}
                            </OKTableCell>
                        </OKTableRow>
                    ));
                })
            ),
        );
    }

    buildTableFooter(assets) {
        const totalAmount = this.sumAmounts(assets);

        return (
            <OKTableRow className="cm-funds-summary__footer-row">
                <OKTableCell className="cm-funds-summary__footer-cell cm-funds-summary__footer-cell--first clearfix">
                    <FormattedText
                        value="brokerCM-fundsSummaryAmountToInvest"
                        className="cm-funds-summary__footer-content-first"
                    />
                    <div className="visible-xs pull-right">
                        <OneLineSymbolAmount
                            amountObject={totalAmount}
                            className="cm-funds-summary__footer-content-last"
                            language={getLanguage()}
                            amountObjectFormatted={formatAmountObject(
                                totalAmount,
                                true,
                            )}
                        />
                    </div>
                </OKTableCell>
                <OKTableCell className="cm-funds-summary__footer-cell hidden-xs"></OKTableCell>
                <OKTableCell className="cm-funds-summary__footer-cell hidden-xs"></OKTableCell>
                <OKTableCell className="cm-funds-summary__footer-cell cm-funds-summary__footer-cell--last hidden-xs">
                    <OneLineSymbolAmount
                        amountObject={totalAmount}
                        className="cm-funds-summary__footer-content-last"
                        language={getLanguage()}
                        amountObjectFormatted={formatAmountObject(
                            totalAmount,
                            true,
                        )}
                    />
                </OKTableCell>
            </OKTableRow>
        );
    }

    render() {
        const { portfolio, hideTotal } = this.props;
        const assets = portfolio ? portfolio.get('assetClassInfo') : [];
        const tableHeader = this.buildTableHeader();
        const tableBody = this.buildTableBody(assets);
        const tableFooter = hideTotal ? '' : this.buildTableFooter(assets);

        return (
            <OKTable className="cm-funds-summary">
                <thead className="cm-funds-summary__table-header">
                    {tableHeader}
                </thead>
                <tbody>{tableBody}</tbody>
                <tfoot>{tableFooter}</tfoot>
            </OKTable>
        );
    }
}

CmFundsSummaryStep1.propTypes = {
    isFundsLink: PropTypes.bool,
    portfolio: PropTypes.object,
    onLegalDocumentClick: PropTypes.func,
    hideTotal: PropTypes.bool,
    hideDocument: PropTypes.bool,
    showGeneralMinAmountMessage: PropTypes.bool,
    isFetchingPortfolioInfo: PropTypes.bool,
    originFundMatchedIsin: PropTypes.string,
};

module.exports = CmFundsSummaryStep1;
